/**
 * useCircularNavigation – manages keyboard-driven navigation, the wrap-around index state
 * machine, and animation timing for a circular list. Extracted from useCircularList so the
 * index/keyboard state machine can be reasoned about independently of rendering.
 */
import DeviceConfig from '@devices/config';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Constants
 *
 * MIN_NAV_INTERVAL_MS: Hard floor between accepted nav key-presses, independent of press-and-hold repeat timing —
 * discrete taps reset their "pressures" count on keyup, so without this floor a burst of
 * separate taps had no time-based throttle at all.
 * POST_WRAP_BLOCK_UNTIL: The time in milliseconds to block key presses after a wrap. (60 milliseconds)
 */
const MIN_NAV_INTERVAL_MS = 0;
const POST_WRAP_BLOCK_UNTIL = 60;

/**
 * Props for useCircularNavigation.
 */
export interface CircularNavigationProps<T> {
  direction?: Direction;
  duration: number;
  focus?: Focus | number;
  initialIndex?: number;
  items: T[];
  onChange?: (index: number) => void;
  onEnter?: (item: T, index: number) => void;
  onExit?: () => void;
  onKeyDown?: () => void;
  onKeyUp?: () => void;
}

/**
 * Custom hook to manage keyboard-driven navigation and index state for a circular list.
 * @param props - Props for useCircularNavigation.
 * @returns {Object} The current index/duration, the wrap-snap handler, and index-mapping helpers.
 */
export default function useCircularNavigation<T>({
  direction = Direction.HORIZONTAL,
  duration: originalDuration,
  focus,
  initialIndex = 0,
  items,
  onChange,
  onEnter,
  onExit,
  onKeyDown,
  onKeyUp,
}: CircularNavigationProps<T>) {
  // ------------------------------------------------------------
  // Initialization
  // ------------------------------------------------------------

  // Selectors
  const currentFocus = useAppSelector(selectFocus);

  const length = items?.length ?? 0;
  // A single item has nowhere to wrap to, so no duplicates and no circular navigation.
  const noDuplicates =
    DeviceConfig.capabilities.disableAnimations ||
    !originalDuration ||
    length <= 1;
  const circularLength = noDuplicates ? length : length + 2;

  // State: when no duplicates use logical index (0..length-1); otherwise internal index (0..length+1)
  const [duration, setDuration] = useState(originalDuration);
  const [index, setIndex] = useState(() => {
    const safe = Math.min(
      Math.max(0, initialIndex),
      Math.max(0, (items?.length ?? 0) - 1)
    );
    return noDuplicates ? safe : safe + 1;
  });

  // Variables
  const focused =
    focus === currentFocus ||
    (Array.isArray(currentFocus) &&
      ((currentFocus as (number | string)[]).includes(focus as number) ||
        (currentFocus as (number | string)[]).includes(String(focus))));

  // Refs
  const animatingRef = useRef<boolean>(false); // Whether the list is currently animating.
  const blockPressUntilRef = useRef<number>(0); // Prevents multiple key presses from moving the index multiple times.
  const circularLengthRef = useRef(circularLength);
  const focusedRef = useRef(focused); // Whether the list is currently focused.
  const indexRef = useRef<number>(index); // The current index.
  const itemsRef = useRef(items);
  const lastReportedRef = useRef<number>(initialIndex); // The last reported logical index.
  const lastMoveTimeRef = useRef<number>(0); // The last time the index was moved.
  const lengthRef = useRef(length);
  const pendingSyncRef = useRef<number | null>(null); // Deferred parent-driven index sync, applied once the in-flight transition settles.
  circularLengthRef.current = circularLength;
  focusedRef.current = focused;
  indexRef.current = index;
  itemsRef.current = items;
  lengthRef.current = length;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  // Converts a logical index to an internal index (no-op when there are no duplicates).
  const logicalToInternal = useCallback(
    (logical: number) =>
      noDuplicates ? logical : Math.min(logical + 1, length),
    [noDuplicates, length]
  );

  // Converts an internal index to a logical index (identity when there are no duplicates).
  const internalToLogical = useCallback(
    (internal: number) =>
      noDuplicates
        ? internal
        : internal === 0
        ? lengthRef.current - 1
        : internal === circularLengthRef.current - 1
        ? 0
        : internal - 1,
    [noDuplicates]
  );

  // Reports the current logical index when the index changes.
  const reportChange = useCallback(
    (logicalIndex: number) => {
      if (lastReportedRef.current === logicalIndex) return;
      lastReportedRef.current = logicalIndex;
      onChange?.(logicalIndex);
    },
    [onChange]
  );

  // Moves the index by a given delta. When there are no duplicates, index is logical and wraps 0..length-1.
  const moveByOffset = useCallback(
    (delta: number) => {
      if (!lengthRef.current) return;
      const len = lengthRef.current;
      setIndex((prev) => {
        if (noDuplicates) {
          return (prev + delta + len) % len;
        }
        let next = prev + delta;
        if (next === 0 && onExit) {
          onExit();
          animatingRef.current = false;
          return prev;
        }
        if (next < 0) next = circularLengthRef.current - 1;
        if (next > circularLengthRef.current - 1)
          next = circularLengthRef.current - 1;
        return next;
      });
    },
    [noDuplicates]
  );

  // Tries to move the index by a given key. Accepted at most once every MIN_NAV_INTERVAL_MS.
  const navigate = useCallback(
    (key: string) => {
      if (animatingRef.current) return;
      // A single item can't navigate anywhere.
      if (lengthRef.current <= 1) return;
      const now = Date.now();
      if (now < blockPressUntilRef.current) return;
      const minIntervalMs = Math.max(
        MIN_NAV_INTERVAL_MS,
        originalDuration * 1000
      );
      if (now - lastMoveTimeRef.current < minIntervalMs) return;
      const animationsDisabled = DeviceConfig.capabilities.disableAnimations;
      if (!animationsDisabled) animatingRef.current = true;
      lastMoveTimeRef.current = now;
      if (direction === Direction.HORIZONTAL) {
        if (key === 'RIGHT') moveByOffset(1);
        else if (key === 'LEFT') moveByOffset(-1);
      } else {
        if (key === 'DOWN') moveByOffset(1);
        else if (key === 'UP') moveByOffset(-1);
      }
    },
    [direction, originalDuration, moveByOffset]
  );

  // Applies a parent-driven (external) index change — e.g. arrow click or auto-advance —
  // respecting the duplicate-boundary special cases and arming the same animating mutex
  // keyboard navigation uses, so the two index sources can't fight mid-transition.
  const applyExternalSync = useCallback((nextInternal: number) => {
    setIndex((prev) => {
      if (prev === 0 || prev === circularLengthRef.current - 1) return prev;
      if (
        prev !== nextInternal &&
        prev === lengthRef.current &&
        nextInternal === 1
      )
        return circularLengthRef.current - 1;
      if (
        prev !== nextInternal &&
        prev === 1 &&
        nextInternal === lengthRef.current
      )
        return 0;
      if (prev === nextInternal) return prev;
      if (!DeviceConfig.capabilities.disableAnimations) {
        animatingRef.current = true;
        lastMoveTimeRef.current = Date.now();
      }
      return nextInternal;
    });
  }, []);

  // Applies a parent-driven sync that arrived while a keyboard-driven transition was in
  // flight and had to be deferred (see the sync effect below).
  const drainPendingSync = useCallback(() => {
    if (pendingSyncRef.current === null) return;
    const next = pendingSyncRef.current;
    pendingSyncRef.current = null;
    applyExternalSync(next);
  }, [applyExternalSync]);

  // Handles the transition end event (no-op when there are no duplicates to snap).
  const onTransitionEnd = useCallback(() => {
    animatingRef.current = false;
    if (noDuplicates || !lengthRef.current) {
      drainPendingSync();
      return;
    }
    // Right wrap: was at right duplicate (length+1) -> snap to real first item (internal index: 1)
    if (index === circularLengthRef.current - 1) {
      setDuration(0);
      setIndex(1);
      lastReportedRef.current = 0;
      blockPressUntilRef.current = Date.now() + POST_WRAP_BLOCK_UNTIL;
      setTimeout(() => {
        setDuration(originalDuration);
        lastMoveTimeRef.current = 0;
      }, 50);
      drainPendingSync();
      return;
    }
    // Left wrap: was at left duplicate (0) -> snap to real last (internal length)
    if (index === 0) {
      setDuration(0);
      setIndex(lengthRef.current);
      lastReportedRef.current = lengthRef.current - 1;
      blockPressUntilRef.current = Date.now() + POST_WRAP_BLOCK_UNTIL;
      setTimeout(() => {
        setDuration(originalDuration);
        lastMoveTimeRef.current = 0;
      }, 50);
      drainPendingSync();
      return;
    }
    drainPendingSync();
  }, [noDuplicates, index, originalDuration, drainPendingSync]);

  // ------------------------------------------------------------
  // Effects
  // ------------------------------------------------------------

  // When animations are disabled, transitionEnd never fires; run wrap-snap and clear logic after index updates.
  useEffect(() => {
    if (!DeviceConfig.capabilities.disableAnimations) return;
    onTransitionEnd();
  }, [index, onTransitionEnd]);

  // Syncs the index from the parent (e.g. timer / auto-advance). Deferred while a
  // keyboard-driven transition is in flight — applying it immediately would stomp the
  // animatingRef mutex `navigate` relies on and desync the tracked index, causing the
  // list to visibly oscillate between two positions.
  useEffect(() => {
    if (!length) return;
    const logical = Math.min(Math.max(0, initialIndex), length - 1);
    if (noDuplicates) {
      setIndex(logical);
      return;
    }
    const nextInternal = logicalToInternal(logical);
    if (animatingRef.current) {
      pendingSyncRef.current = nextInternal;
      return;
    }
    applyExternalSync(nextInternal);
  }, [
    noDuplicates,
    length,
    initialIndex,
    logicalToInternal,
    applyExternalSync,
  ]);

  // Report current logical index when index changes; skip when no items to avoid reporting -1
  useEffect(() => {
    if (!lengthRef.current) return;
    const logical = internalToLogical(index);
    if (logical < 0 || logical >= lengthRef.current) return;
    reportChange(logical);
  }, [index, internalToLogical, reportChange]);

  // ------------------------------------------------------------
  // Keyboard
  // ------------------------------------------------------------

  // Checks if a key is a navigation key.
  const isNavKey = useCallback(
    (key: string) => {
      if (direction === Direction.HORIZONTAL)
        return key === 'LEFT' || key === 'RIGHT';
      return key === 'UP' || key === 'DOWN';
    },
    [direction]
  );

  // Handles the keyboard events.
  useKeyboard({
    focus,
    keyDownHandler: (key) => {
      if (!focusedRef.current || !lengthRef.current) return;
      if (key === 'SELECT') {
        const logical = internalToLogical(indexRef.current);
        onEnter?.(itemsRef.current[logical], logical);
        return;
      }
      if (!isNavKey(key)) return;
      onKeyDown?.();
      navigate(key);
    },
    keyUpHandler: (key) => {
      if (isNavKey(key)) onKeyUp?.();
    },
  });

  return {
    duration,
    index,
    noDuplicates,
    internalToLogical,
    onTransitionEnd,
  };
}
